/**
 * Enhanced Shopify Variant Price Updater
 * 
 * This is an improved version of the price updater script that fixes the issues
 * with variant price updates on the Shopify product pages.
 */

document.addEventListener('DOMContentLoaded', function() {
  // Debug function with better formatting and timestamp
  function debug(message) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    console.log(`[Price Updater ${timestamp}]`, message);
  }
  
  debug('Enhanced price updater initialized');
  
  // Format price function with better locale handling
  function formatPrice(price) {
    return (price / 100).toFixed(2).replace('.', ',');
  }
  
  // Main function to update prices with improved error handling and structure
  function updatePrices(variant) {
    if (!variant) {
      debug('No variant provided to updatePrices');
      return;
    }
    
    debug(`Updating prices for variant: ${variant.id} (${variant.title || 'Unknown'})`);
    
    // Find all price containers (there might be multiple on the page)
    const containers = document.querySelectorAll('[data-product-price-container]');
    if (containers.length === 0) {
      debug('No price containers found on page');
      return;
    }
    
    debug(`Found ${containers.length} price container(s)`);
    
    // Update each container
    containers.forEach((container, index) => {
      try {
        updatePriceContainer(container, variant, index);
      } catch (error) {
        debug(`Error updating container ${index}: ${error.message}`);
      }
    });
  }
  
  // Function to update a single price container
  function updatePriceContainer(container, variant, containerIndex) {
    // Get price elements
    const comparePrice = container.querySelector('[data-compare-price]');
    const pixPrice = container.querySelector('[data-price-pix]');
    const regularPrice = container.querySelector('[data-price-regular]');
    const installmentPrice = container.querySelector('[data-price-installment]');
    const offerTag = container.querySelector('.offer-tag');
    
    // Calculate prices
    const pixDiscount = variant.price * 0.95;
    const installmentValue = (variant.price * 1.0299) / 12;
    
    debug(`Container ${containerIndex} - Variant price: ${variant.price}, Compare at price: ${variant.compare_at_price || 'N/A'}`);
    
    // Update DOM elements based on whether there's a sale
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      debug(`Container ${containerIndex} - This variant is on sale`);
      
      // Show offer tag
      if (offerTag) {
        offerTag.style.display = 'inline-block';
      }
      
      // Update compare price
      if (comparePrice) {
        comparePrice.innerHTML = `R$ ${formatPrice(variant.compare_at_price)}`;
        comparePrice.style.display = 'inline';
      }
      
      // Check if we need to restructure the DOM for a sale item
      ensureSaleStructure(container);
    } else {
      debug(`Container ${containerIndex} - This variant is not on sale`);
      
      // Hide offer tag
      if (offerTag) {
        offerTag.style.display = 'none';
      }
      
      // Hide compare price
      if (comparePrice) {
        comparePrice.style.display = 'none';
      }
      
      // Check if we need to restructure the DOM for a non-sale item
      ensureRegularStructure(container);
    }
    
    // Always update these prices
    if (pixPrice) {
      pixPrice.innerHTML = `R$ ${formatPrice(pixDiscount)}`;
      debug(`Container ${containerIndex} - Updated PIX price: ${formatPrice(pixDiscount)}`);
    }
    
    if (regularPrice) {
      regularPrice.innerHTML = `R$ ${formatPrice(variant.price)}`;
      debug(`Container ${containerIndex} - Updated regular price: ${formatPrice(variant.price)}`);
    }
    
    if (installmentPrice) {
      installmentPrice.innerHTML = `12x* de R$ ${formatPrice(installmentValue)}`;
      debug(`Container ${containerIndex} - Updated installment price: ${formatPrice(installmentValue)}`);
    }
  }
  
  // Ensure the DOM structure is correct for a sale item
  function ensureSaleStructure(container) {
    // This function would restructure the DOM if needed for a sale item
    // For now, we'll just log that we would do this
    debug('Would ensure sale structure if needed');
  }
  
  // Ensure the DOM structure is correct for a non-sale item
  function ensureRegularStructure(container) {
    // This function would restructure the DOM if needed for a non-sale item
    // For now, we'll just log that we would do this
    debug('Would ensure regular structure if needed');
  }
  
  // Get variants data with improved error handling and multiple sources
  let productVariants = [];
  
  // Try multiple possible locations for variant data
  function loadVariantsData() {
    debug('Attempting to load variants data from multiple sources');
    
    // Method 1: From .pr_variants_json element
    const variantsJson = document.querySelector('.pr_variants_json');
    if (variantsJson) {
      try {
        productVariants = JSON.parse(variantsJson.textContent);
        debug(`Found ${productVariants.length} variants from .pr_variants_json`);
        return true;
      } catch (e) {
        debug(`Error parsing variants JSON from .pr_variants_json: ${e.message}`);
      }
    }
    
    // Method 2: From window.meta object (common in many Shopify themes)
    if (window.meta && window.meta.product && window.meta.product.variants) {
      productVariants = window.meta.product.variants;
      debug(`Found ${productVariants.length} variants from window.meta`);
      return true;
    }
    
    // Method 3: From any script tag containing product JSON
    const scriptTags = document.querySelectorAll('script[type="application/json"]');
    for (let i = 0; i < scriptTags.length; i++) {
      try {
        const data = JSON.parse(scriptTags[i].textContent);
        if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
          productVariants = data.variants;
          debug(`Found ${productVariants.length} variants from script tag #${i}`);
          return true;
        }
      } catch (e) {
        // Silently continue to next script tag
      }
    }
    
    // Method 4: Look for T4S specific data
    if (window.t4sData && window.t4sData.product && window.t4sData.product.variants) {
      productVariants = window.t4sData.product.variants;
      debug(`Found ${productVariants.length} variants from t4sData`);
      return true;
    }
    
    debug('Failed to find variants data from any source');
    return false;
  }
  
  // Function to find variant by ID with better error handling
  function findVariantById(variantId) {
    if (!variantId) {
      debug('No variant ID provided to findVariantById');
      return null;
    }
    
    // Convert to string for comparison
    const idStr = variantId.toString();
    debug(`Looking for variant with ID: ${idStr}`);
    
    // First try exact match
    let variant = productVariants.find(v => v.id.toString() === idStr);
    
    // If not found, try without the first digit (some Shopify themes modify the ID)
    if (!variant && idStr.length > 1) {
      const altId = idStr.substring(1);
      debug(`Trying alternative ID matching (without first digit): ${altId}`);
      variant = productVariants.find(v => v.id.toString().endsWith(altId));
      if (variant) {
        debug(`Found variant using alternative ID matching: ${variant.id}`);
      }
    }
    
    if (!variant) {
      debug(`Could not find variant with ID: ${idStr}`);
    } else {
      debug(`Found variant: ${variant.id} (${variant.title || 'Unknown'})`);
    }
    
    return variant;
  }
  
  // Load variants data
  if (!loadVariantsData()) {
    debug('Initial variant data load failed, will retry after delay');
    setTimeout(loadVariantsData, 1000);
  }
  
  // Create a global function that can be called from the theme's code
  window.updateCustomPrices = function(variantId) {
    debug(`updateCustomPrices called with ID: ${variantId}`);
    if (variantId && productVariants.length > 0) {
      const currentVariant = findVariantById(variantId);
      if (currentVariant) {
        updatePrices(currentVariant);
      } else {
        debug('Variant not found in product variants');
        
        // Fallback: Try to get variant info from the DOM
        tryGetVariantFromDOM();
      }
    }
  };
  
  // Try to extract variant information from the DOM when variant lookup fails
  function tryGetVariantFromDOM() {
    debug('Attempting to extract variant info from DOM elements');
    
    // Look for price elements that might contain the current prices
    const priceElement = document.querySelector('.t4s-product-price__price');
    const compareElement = document.querySelector('.t4s-product-price__compare');
    
    if (priceElement) {
      try {
        // Extract price from the element (assuming format like "R$ 123,45")
        const priceText = priceElement.textContent.trim();
        const priceMatch = priceText.match(/[\d,.]+/);
        
        if (priceMatch) {
          // Convert price string to number (e.g., "123,45" to 12345)
          const price = parseFloat(priceMatch[0].replace(',', '.')) * 100;
          
          // Create a synthetic variant object
          const syntheticVariant = { 
            id: 'dom-extracted',
            price: price,
            compare_at_price: null
          };
          
          // If we have a compare price, extract it too
          if (compareElement) {
            const compareText = compareElement.textContent.trim();
            const compareMatch = compareText.match(/[\d,.]+/);
            if (compareMatch) {
              syntheticVariant.compare_at_price = parseFloat(compareMatch[0].replace(',', '.')) * 100;
            }
          }
          
          debug(`Created synthetic variant from DOM: ${JSON.stringify(syntheticVariant)}`);
          updatePrices(syntheticVariant);
          return true;
        }
      } catch (e) {
        debug(`Error extracting price from DOM: ${e.message}`);
      }
    }
    
    return false;
  }
  
  // Function to capture the current variant from multiple possible sources
  function captureCurrentVariant() {
    debug('Attempting to capture current variant from multiple sources');
    
    // Method 1: From input[name="id"]
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      const variantId = variantInput.value;
      debug(`Found variant ID from input: ${variantId}`);
      window.updateCustomPrices(variantId);
      return;
    }
    
    // Method 2: From data attribute on form
    const productForm = document.querySelector('form[data-product]');
    if (productForm && productForm.getAttribute('data-variant-id')) {
      const variantId = productForm.getAttribute('data-variant-id');
      debug(`Found variant ID from form data attribute: ${variantId}`);
      window.updateCustomPrices(variantId);
      return;
    }
    
    // Method 3: From T4S specific data
    if (window.t4sData && window.t4sData.current_variant_id) {
      debug(`Found variant ID from t4sData: ${window.t4sData.current_variant_id}`);
      window.updateCustomPrices(window.t4sData.current_variant_id);
      return;
    }
    
    // Method 4: From URL params (some themes use this)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) {
      const variantId = urlParams.get('variant');
      debug(`Found variant ID from URL: ${variantId}`);
      window.updateCustomPrices(variantId);
      return;
    }
    
    // Method 5: From selected option in product-select dropdown
    const productSelect = document.getElementById('product-select-' + window.location.pathname.split('/').pop());
    if (productSelect && productSelect.value) {
      const variantId = productSelect.value;
      debug(`Found variant ID from product-select dropdown: ${variantId}`);
      window.updateCustomPrices(variantId);
      return;
    }
    
    debug('Could not find current variant ID from any source');
    tryGetVariantFromDOM();
  }
  
  // Set up event listeners for T4S theme
  function setupT4SEventListeners() {
    debug('Setting up T4S specific event listeners');
    
    // Listen for T4S custom event
    document.addEventListener('t4s:variant:change', function(event) {
      debug('t4s:variant:change event detected');
      if (event.detail && event.detail.variant) {
        debug(`Variant from event: ${event.detail.variant.id}`);
        updatePrices(event.detail.variant);
      }
    });
    
    // Add direct click handlers to swatch items
    const swatchItems = document.querySelectorAll('[data-swatch-item], .t4s-swatch__item');
    debug(`Found ${swatchItems.length} swatch items`);
    
    if (swatchItems.length > 0) {
      swatchItems.forEach(item => {
        item.addEventListener('click', function() {
          const value = this.getAttribute('data-value') || this.textContent.trim();
          debug(`Swatch item clicked: ${value}`);
          
          // Wait a short time for the theme to update the variant ID
          setTimeout(captureCurrentVariant, 100);
        });
      });
    }
    
    // Add a global click handler to catch all potential variant changes
    document.addEventListener('click', function(event) {
      // Check if the click was on or inside a swatch-related element
      let target = event.target;
      while (target != null) {
        if (target.hasAttribute && (
            target.hasAttribute('data-swatch-item') || 
            target.hasAttribute('data-dropdown-item') ||
            target.classList.contains('t4s-swatch__item') ||
            target.classList.contains('t4s-product-option-item')
          )) {
          debug('Detected click on swatch-related element');
          
          // Wait for the theme to update the variant
          setTimeout(captureCurrentVariant, 150);
          break;
        }
        target = target.parentElement;
      }
    });
    
    // Monitor the variant input for changes
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      debug(`Found variant input with initial value: ${variantInput.value}`);
      
      // Use MutationObserver to watch for value changes
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const variantId = variantInput.value;
            debug(`Variant input value changed to: ${variantId}`);
            window.updateCustomPrices(variantId);
          }
        });
      });
      
      observer.observe(variantInput, { attributes: true });
      
      // Also listen for standard change events
      variantInput.addEventListener('change', function() {
        debug(`Variant input change event: ${this.value}`);
        window.updateCustomPrices(this.value);
      });
      
      // Initial price update
      window.updateCustomPrices(variantInput.value);
    } else {
      debug('Variant input not found for initial setup');
      // Try other methods to get the initial variant
      captureCurrentVariant();
    }
  }
  
  // Hook into T4S theme functions
  function hookIntoT4STheme() {
    debug('Attempting to hook into T4S theme functions');
    
    try {
      // Wait for the theme's JS to initialize
      setTimeout(function() {
        if (window.T4SThemeSP && window.T4SThemeSP.ProductSection) {
          debug('Found T4SThemeSP.ProductSection, attempting to extend');
          
          // Store the original function
          const originalUpdateVariant = window.T4SThemeSP.ProductSection.prototype._updateVariant;
          
          // Replace with our extended version
          window.T4SThemeSP.ProductSection.prototype._updateVariant = function(variant) {
            // Call the original function
            const result = originalUpdateVariant.apply(this, arguments);
            
            // Then call our price update
            if (variant) {
              debug(`Intercepted _updateVariant call with variant: ${variant.id}`);
              updatePrices(variant);
            }
            
            return result;
          };
          
          debug('Successfully extended theme variant update function');
        } else {
          debug('T4SThemeSP.ProductSection not found');
        }
      }, 1000);
    } catch (e) {
      debug(`Error trying to hook into theme functions: ${e.message}`);
    }
  }
  
  // Set up DOM observers
  function setupDOMObservers() {
    debug('Setting up DOM observers');
    
    // Observer for product form changes
    const productFormObserver = new MutationObserver(function(mutations) {
      debug('Product form DOM changes detected');
      captureCurrentVariant();
    });
    
    // Start observing the product form
    const productForm = document.querySelector('.t4s-form__product, form[data-product]');
    if (productForm) {
      debug('Found product form, setting up observer');
      productFormObserver.observe(productForm, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-value', 'value', 'data-variant-id']
      });
    } else {
      debug('Product form not found for observation');
    }
    
    // Observer for T4S variant selector
    const t4sVariantSelector = document.querySelector('[data-callBackVariant], .t4s-product-form__variants');
    if (t4sVariantSelector) {
      debug('Found T4S variant selector, setting up observer');
      
      const selectorObserver = new MutationObserver(function() {
        debug('T4S variant selector DOM changes detected');
        captureCurrentVariant();
      });
      
      selectorObserver.observe(t4sVariantSelector, { 
        childList: true, 
        subtree: true,
        attributes: true
      });
    } else {
      debug('T4S variant selector not found');
    }
  }
  
  // Initialize everything
  function initialize() {
    debug('Initializing price updater');
    
    // Load variants data
    if (loadVariantsData()) {
      debug('Successfully loaded variants data');
    } else {
      debug('Failed to load variants data, will retry after delay');
      setTimeout(function() {
        if (loadVariantsData()) {
          debug('Successfully loaded variants data after delay');
        } else {
          debug('Failed to load variants data even after delay');
        }
      }, 1000);
    }
    
    // Set up event listeners
    setupT4SEventListeners();
    
    // Hook into theme functions
    hookIntoT4STheme();
    
    // Set up DOM observers
    setupDOMObservers();
    
    // Initial price update
    captureCurrentVariant();
    
    // Periodic check for the first few seconds
    let checkCount = 0;
    const priceUpdateInterval = setInterval(function() {
      debug(`Periodic price check: ${checkCount}`);
      captureCurrentVariant();
      
      checkCount++;
      if (checkCount >= 5) {
        clearInterval(priceUpdateInterval);
        debug('Stopped periodic price checks');
      }
    }, 1000);
  }
  
  // Start everything
  initialize();
});